import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import Select from '@codegouvfr/react-dsfr/Select';
import clsx from 'clsx';
import {
  hasNewerLaunchedCampaign,
  type ProgrammingPlanChecked,
  ProgrammingPlanSort,
  programmingPlanContexts
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { useContext, useEffect, useMemo, useState } from 'react';
import dashboard from 'src/assets/illustrations/dashboard.svg';
import { AppPage } from 'src/components/_app/AppPage/AppPage';
import { useAuthentication } from 'src/hooks/useAuthentication';
import ProgrammingPlanCard from 'src/views/DashboardView/ProgrammingPlanCard';
import { AuthenticatedAppRoutes } from '../../AppRoutes';
import { ApiClientContext } from '../../services/apiClient';
import DashboardComplianceStats from './DashboardComplianceStats';
import DashboardNoticeAndActions from './DashboardNoticeAndActions';
import DashboardPrescriptions from './DashboardPrescriptions';
import DashboardResidueStats from './DashboardResidueStats';

const DashboardView = () => {
  const apiClient = useContext(ApiClientContext);
  const { hasUserPermission, user, hasNationalView, hasRole } =
    useAuthentication();

  const { data: programmingPlans } = apiClient.useFindProgrammingPlansQuery(
    {
      status: ['Validated', 'Closed'],
      scope: 'owned'
    },
    {
      skip:
        !user?.programmingSubPlans?.length &&
        !hasRole('AdministratorMaestro', 'AdministratorBGIR')
    }
  );

  const { data: programmingPlanDomains } =
    apiClient.useFindProgrammingPlanDomainsQuery();

  const domainLabelById = useMemo(
    () =>
      new Map(
        (programmingPlanDomains ?? []).map(({ id, label }) => [id, label])
      ),
    [programmingPlanDomains]
  );

  const [currentValidatedProgrammingPlan, setCurrentValidatedProgrammingPlan] =
    useState<ProgrammingPlanChecked>();

  useEffect(() => {
    setCurrentValidatedProgrammingPlan(
      [...(programmingPlans ?? [])]
        .sort(ProgrammingPlanSort)
        .filter((pp) => pp.year <= new Date().getFullYear())[0]
    );
  }, [programmingPlans]);

  if (!user) {
    return null;
  }

  return (
    <AppPage
      title="Tableau de bord"
      subtitle="Un rapide coup d'oeil sur votre activité"
      illustration={dashboard}
      documentTitle="Tableau de bord"
      action={
        <>
          {(programmingPlans?.length ?? 0) > 1 && (
            <Select
              label=""
              nativeSelectProps={{
                value: currentValidatedProgrammingPlan?.id ?? '',
                onChange: (e) =>
                  setCurrentValidatedProgrammingPlan(
                    programmingPlans?.find((plan) => plan.id === e.target.value)
                  )
              }}
              className={clsx(cx('fr-pt-4w'))}
            >
              {programmingPlans?.map((plan) => (
                <option key={`plan-${plan.id}`} value={plan.id}>
                  {plan.title} {plan.year}
                </option>
              ))}
            </Select>
          )}
          {currentValidatedProgrammingPlan &&
            !hasNewerLaunchedCampaign(
              currentValidatedProgrammingPlan,
              programmingPlans ?? [],
              domainLabelById
            ) &&
            hasUserPermission('createSample') && (
              <Button
                size="large"
                linkProps={{
                  to: AuthenticatedAppRoutes.NewSampleRoute.link(
                    currentValidatedProgrammingPlan.year
                  ),
                  target: '_self'
                }}
                iconId="fr-icon-microscope-line"
              >
                Saisir un prélèvement
              </Button>
            )}
        </>
      }
    >
      <DashboardNoticeAndActions
        currentValidatedProgrammingPlan={currentValidatedProgrammingPlan}
      />
      <div className={clsx(cx('fr-grid-row', 'fr-grid-row--gutters'))}>
        {hasNationalView &&
          currentValidatedProgrammingPlan &&
          programmingPlanContexts(currentValidatedProgrammingPlan).map(
            (context) => (
              <div
                className={cx('fr-col-12', 'fr-col-md-6')}
                key={`${currentValidatedProgrammingPlan.id}-${context}`}
              >
                <ProgrammingPlanCard
                  programmingPlan={currentValidatedProgrammingPlan}
                  context={context}
                />
              </div>
            )
          )}

        {currentValidatedProgrammingPlan && (
          <DashboardPrescriptions
            programmingPlan={currentValidatedProgrammingPlan}
            className={clsx(cx('fr-col-12'))}
          />
        )}

        {hasNationalView && currentValidatedProgrammingPlan && (
          <DashboardResidueStats
            programmingPlan={currentValidatedProgrammingPlan}
          />
        )}
        {hasNationalView && currentValidatedProgrammingPlan && (
          <DashboardComplianceStats
            programmingPlan={currentValidatedProgrammingPlan}
          />
        )}
      </div>
    </AppPage>
  );
};

export default DashboardView;
